import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Aurora } from "../components/Aurora";
import { Card } from "../components/Card";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { useAuth } from "../lib/auth.jsx";
import { getSupabase } from "../lib/supabase";
import { STUDIO } from "../data/constants";

export function AuthScreen({ mode = "login", theme = "dark" }) {
  const isSignUp = mode === "signup";
  const isDark = theme === "dark";
  const isCompact = useMediaQuery("(max-width: 760px)");
  const { signIn, signUp, resendVerificationByEmail } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [oauthBusy, setOauthBusy] = useState("");
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");

  useEffect(() => {
    if (searchParams.get("verified") !== "1") return;
    const verifiedEmail = searchParams.get("email");
    if (verifiedEmail) setEmail(verifiedEmail);
    setInfo("Email verified. Sign in to continue.");
    setErr("");
  }, [searchParams]);

  async function submit(e) {
    e.preventDefault();
    setErr(""); setInfo(""); setBusy(true);
    try {
      if (isSignUp) {
        await signUp(email, password, name);
        setInfo("Check your email to confirm your account. The link expires in 5 minutes.");
      } else {
        await signIn(email, password);
        nav("/");
      }
    } catch (ex) {
      const message = ex.message || String(ex);
      setErr(message);
      if (!isSignUp && email && /confirm|verify|verification|email/i.test(message)) {
        try {
          await resendVerificationByEmail(email);
          setInfo("We sent a fresh confirmation email if that account exists. The link expires in 5 minutes.");
        } catch {
          // Keep the original sign-in error visible.
        }
      }
    }
    setBusy(false);
  }

  async function magicLink() {
    setErr(""); setInfo(""); setBusy(true);
    try {
      const r = await fetch("/api/auth/magic-link", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const j = await r.json();
      setInfo(j.message || "Check your email.");
    } catch (ex) { setErr(ex.message); }
    setBusy(false);
  }

  async function loginWith(provider) {
    setErr("");
    setOauthBusy(provider);
    try {
      const sb = await getSupabase();
      await sb.auth.signInWithOAuth({ provider, options: { redirectTo: `${window.location.origin}/verify-email` } });
    } catch (ex) {
      setErr(ex.message);
      setOauthBusy("");
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", color: "var(--text)", position: "relative", overflow: "hidden" }}>
      <Aurora variant={isDark ? "noir" : "ivory"} />
      <div style={{
        position: "relative",
        zIndex: 1,
        minHeight: isCompact ? "auto" : "100vh",
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 420px), 1fr))",
        alignItems: isCompact ? "start" : "center",
        gap: isCompact ? 20 : 28,
        maxWidth: 1120,
        margin: "0 auto",
        padding: isCompact ? "24px 16px 118px" : "clamp(28px, 6vw, 72px) 16px",
      }}>
        <section style={{ display: "grid", gap: isCompact ? 16 : 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <img
              src="/app-icons/oge-app-icon-photoreal.png"
              alt=""
              style={{ width: 48, height: 48, borderRadius: 14, boxShadow: "var(--shadow-md)" }}
            />
            <div>
              <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1.6, color: "var(--gold)", textTransform: "uppercase" }}>
                Oge Eyesthetic
              </div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 19, fontWeight: 800, color: "var(--text)" }}>{STUDIO}</div>
            </div>
          </div>

          <div>
            <h1 style={{
              fontFamily: "var(--font-display)",
              fontSize: isCompact ? "clamp(34px, 10vw, 46px)" : "clamp(42px, 7vw, 76px)",
              lineHeight: 0.96,
              letterSpacing: 0,
              maxWidth: 720,
              margin: 0,
              color: "var(--text)",
            }}>
              Your nail studio, booking desk, and design archive in one place.
            </h1>
            <p style={{
              margin: "18px 0 0",
              maxWidth: 560,
              color: "var(--text-muted)",
              fontSize: isCompact ? 15 : 16,
              lineHeight: isCompact ? 1.55 : 1.75,
            }}>
              Generate realistic nail art, keep every appointment organized, and return to the exact look you loved.
            </p>
          </div>

          <div style={{ display: isCompact ? "none" : "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 170px), 1fr))", gap: 10, maxWidth: 620 }}>
            {[
              ["AI looks", "Photoreal previews before you book"],
              ["Fast pay", "Cash, transfer, card, and local currency"],
              ["Aftercare", "Reminders, passes, and history"],
            ].map(([title, body]) => (
              <div key={title} style={{
                minHeight: 112,
                borderRadius: 18,
                border: "1px solid var(--border)",
                background: "var(--glass)",
                padding: 15,
                backdropFilter: "blur(18px)",
              }}>
                <div style={{ fontWeight: 900, fontSize: 14, color: "var(--text)" }}>{title}</div>
                <div style={{ marginTop: 8, color: "var(--text-muted)", fontSize: 12.5, lineHeight: 1.5 }}>{body}</div>
              </div>
            ))}
          </div>
        </section>

        <Card style={{
          maxWidth: 430,
          width: "100%",
          margin: 0,
          borderRadius: 24,
          padding: 24,
          ...(isCompact ? { padding: 20, borderRadius: 20 } : null),
          background: "var(--bg-elevated)",
          border: "1px solid var(--border)",
          color: "var(--text)",
          boxShadow: "var(--shadow-lg)",
          backdropFilter: "blur(22px)",
        }}>
          <header style={{ marginBottom: 20 }}>
            <div style={{
              width: 42,
              height: 42,
              borderRadius: 14,
              background: "linear-gradient(135deg, var(--mauve), var(--mauve-deep))",
              color: "#fff",
              display: "grid",
              placeItems: "center",
              fontWeight: 900,
              marginBottom: 14,
            }}>
              {isSignUp ? "01" : "↗"}
            </div>
            <h2 style={{ fontFamily: "var(--font-display)", fontSize: 30, lineHeight: 1.05, margin: 0, color: "var(--text)" }}>
              {isSignUp ? "Create your account" : "Welcome back"}
            </h2>
            <p style={{ margin: "8px 0 0", color: "var(--text-muted)", fontSize: 13.5, lineHeight: 1.6 }}>
              {isSignUp ? "Save designs, book appointments, and manage your history." : `Sign in to continue to ${STUDIO}.`}
            </p>
          </header>
          <form onSubmit={submit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {isSignUp && (
              <Input label="Full Name" required placeholder="Amara Johnson" autoComplete="name"
                value={name} onChange={(e) => setName(e.target.value)} />
            )}
            <Input label="Email" required type="email" placeholder="you@example.com" autoComplete="email"
              value={email} onChange={(e) => setEmail(e.target.value)} />
            <Input label="Password" required type="password" placeholder="At least 8 characters"
              autoComplete={isSignUp ? "new-password" : "current-password"}
              value={password} onChange={(e) => setPassword(e.target.value)} />

            {!isSignUp && (
              <div style={{ textAlign: "right", marginTop: -6 }}>
                <Link to="/forgot-password" style={{ fontSize: 13, color: "var(--accent)", textDecoration: "none", fontWeight: 500 }}>
                  Forgot password?
                </Link>
              </div>
            )}

            {err && (
              <div role="alert" style={{
                fontSize: 13,
                color: "var(--text)",
                background: "var(--status-danger-bg)",
                border: "1px solid var(--status-danger-border)",
                borderRadius: 10,
                padding: "10px 12px"
              }}>
                {err}
              </div>
            )}
            {info && (
              <div style={{ fontSize: 13, color: "var(--text-confirm)", background: "var(--status-warning-bg)", border: "1px solid var(--border-strong)", borderRadius: 10, padding: "10px 12px" }}>
                {info}
              </div>
            )}
            <Button type="submit" full size="lg" loading={busy}
              label={isSignUp ? "Create account" : "Sign in"}
              icon={isSignUp ? "✨" : "→"} />

            {!isSignUp && (
              <Button label="Email me a magic link" icon="✉️" full variant="outline"
                onClick={magicLink} disabled={!email || busy} />
            )}

            <div aria-hidden="true" style={{ display: "flex", alignItems: "center", gap: 10, margin: "6px 0" }}>
              <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
              <span style={{ fontSize: 11, color: "var(--text-muted)" }}>OR</span>
              <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
            </div>

            <Button
              label="Continue with Apple"
              full
              variant="dark"
              icon=""
              loading={oauthBusy === "apple"}
              onClick={() => loginWith("apple")}
            />
            <Button
              label="Continue with Google"
              full
              variant="provider"
              icon="G"
              loading={oauthBusy === "google"}
              onClick={() => loginWith("google")}
            />

            <div style={{ textAlign: "center", fontSize: 13, color: "var(--text-muted)", marginTop: 4 }}>
              {isSignUp ? (
                <>Already have an account? <Link to="/login" style={{ color: "var(--accent)", fontWeight: 600 }}>Sign in</Link></>
              ) : (
                <>New here? <Link to="/signup" style={{ color: "var(--accent)", fontWeight: 600 }}>Create an account</Link></>
              )}
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
}

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => (
    typeof window !== "undefined" ? window.matchMedia(query).matches : false
  ));

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia(query);
    const onChange = (event) => setMatches(event.matches);
    setMatches(mql.matches);
    if (mql.addEventListener) {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    mql.addListener?.(onChange);
    return () => {
      mql.removeListener?.(onChange);
    };
  }, [query]);

  return matches;
}
